
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from "sonner";

export default function AdminLogin() {
  const { signIn, isAdmin, isLoading } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();

  // Get the intended destination or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  // If already authenticated as admin, redirect to intended destination
  if (!isLoading && isAdmin) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      toast.success("Logged in successfully");
      
      // Let the auth context handle the redirect via the Navigate component above
      // No immediate navigation - wait for admin status verification
    } catch (error) {
      console.error('Login error:', error);
      toast.error("Invalid login credentials");
      setIsSubmitting(false);
    }
    // Note: Don't set isSubmitting to false on success - let the redirect handle it
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md p-6">
        <CardHeader className="flex items-center justify-center mb-6">
          <img 
            src="/lovable-uploads/ccf3ee27-56d1-437e-b539-0adb609fe832.png" 
            alt="DriveCourses Logo" 
            className="max-h-24 object-contain mb-4"
          />
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-full"
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-full"
                disabled={isSubmitting}
              />
            </div>
            <button
              type="submit"
              className="w-full bg-[#00dd00] text-white py-2 px-4 rounded-full hover:bg-green-600 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
